import { CircularProgress } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import { Case } from '../../../../shared/api/hscan/case';
import { useReport } from '../../../../shared/hooks/caseHook';
import { BlueRoundedButtonWithIcon } from '../../common/components/BlueRoundedButton';
import { MobileCheckbox } from '../../common/components/CheckboxWithText';
import {
  DownloadButtonProps,
  DownloadCaseButton,
} from '../../common/components/DownloadCaseButton';
import {
  MobileTable,
  MobileTableFooter,
  MobileTableRow,
  MobileTableWrapper,
} from '../../common/components/MobileTable';
import {
  RendererProps,
  TableAndElementInformation,
} from '../../common/components/SortableTable/PresentationalTable';
import {
  renderCell,
  renderHeader,
} from '../../common/components/SortableTable/RenderTable';

import { ImageDetailButtons } from './ImageDetailButtons';
import {
  TableContext,
  TableElementContext,
  TableElementInformationProvider,
} from './ImageListTable';
import { ViewReportModal } from './ViewReportModal';

export const ElementContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: fit-content;
  overflow: auto;
`;

export const HeaderContainer = styled.div`
  display: flex;
  flex: 1;
`;

const CheckboxContainer = styled.div`
  background-color: white;
  padding: 15px;
`;

export const NameContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  background-color: #f5f5f5;
  font-weight: bold;
  font-size: 14px;
`;

export const DownloadButtonContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  padding-top: 30px;
  padding-bottom: 25px;
  border: 1px solid #f5f5f5;
  border-top: none;
`;

export const ImageContainer = styled.div`
  position: relative;
  line-height: 0;
`;

export const ImageActionContainer = styled.div`
  bottom: 9.5px;
  right: 3px;
  position: absolute;
`;

export const MobileImageTableElement = (
  props: TableAndElementInformation<Case, 'caseId'>,
) => {
  const {
    element,
    identifyKey,
    isSelected,
    isSelectable,
    onSelect,
    table,
    row,
  } = props;
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.imageList.myImageList.table',
  });
  const { t: reportTrans } = useTranslation('translation', {
    keyPrefix: 'features.imageList.imageDetails',
  });
  const headerName = useMemo(
    () => (
      <>
        {renderCell(table, row, 'patientName')} /{' '}
        {element.patientSex === 'F'
          ? '여'
          : element.patientSex === 'M'
          ? '남'
          : '기타'}{' '}
        / {renderCell(table, row, 'birthDate')}
      </>
    ),
    [element.patientSex, row, table],
  );

  const [openReport, setOpenReport] = useState(false);
  const { data, error } = useReport(element.caseId);
  const reportPlainText = data?.plainText ?? '';
  const isReportAvailable = !error && reportPlainText !== '';

  return (
    <>
      <ElementContainer>
        <HeaderContainer>
          <CheckboxContainer>
            <MobileCheckbox
              disabled={!isSelectable}
              checked={isSelected}
              onChange={e => onSelect?.(element[identifyKey], e.target.checked)}
            />
          </CheckboxContainer>
          <NameContainer>{headerName}</NameContainer>
        </HeaderContainer>
        <ImageContainer>
          {
            renderCell(table, row, 'contentIds', {
              width: '100%',
              height: '236px',
            }) as React.ReactNode
          }
          <ImageActionContainer>
            <ImageDetailButtons caseInfo={element} />
          </ImageActionContainer>
        </ImageContainer>
        <MobileTable>
          <tbody>
            <MobileTableRow
              label={
                renderHeader(table, row, 'institutionName') as React.ReactNode
              }>
              {renderCell(table, row, 'institutionName') as React.ReactNode}
            </MobileTableRow>
            <MobileTableRow
              label={renderHeader(table, row, 'studyDate') as React.ReactNode}>
              {renderCell(table, row, 'studyDate') as React.ReactNode}
            </MobileTableRow>
            <MobileTableRow
              label={renderHeader(table, row, 'modality') as React.ReactNode}>
              {renderCell(table, row, 'modality') as React.ReactNode}
            </MobileTableRow>
            <MobileTableRow label={`${t('header.bodyPart')}`}>
              {renderCell(table, row, 'bodyPart') as React.ReactNode}
            </MobileTableRow>
          </tbody>
        </MobileTable>
        <DownloadButtonContainer>
          {isReportAvailable && (
            <BlueRoundedButtonWithIcon
              maxWidth="150px"
              onClick={() => setOpenReport(true)}>
              {reportTrans('reportButton')}
            </BlueRoundedButtonWithIcon>
          )}
          <DownloadCaseButton
            caseIds={[element.caseId]}
            DownloadButton={MobileDownloadCaseButtonContent}
          />
        </DownloadButtonContainer>
      </ElementContainer>
      <ViewReportModal
        open={openReport}
        onClose={() => setOpenReport(false)}
        reportPlainText={reportPlainText}
      />
    </>
  );
};

export const MobileImageTable = (props: RendererProps<Case, 'caseId'>) => {
  const { Element = MobileImageTableElement, Footer = MobileTableFooter } =
    props;

  return (
    <MobileTableWrapper
      {...props}
      tableContext={TableContext}
      elementContext={TableElementContext}
      ElementProvider={TableElementInformationProvider}
      Element={Element}
      Footer={Footer}
    />
  );
};

const MobileDownloadCaseButtonContent = ({
  isDownloading,
  onClick,
}: DownloadButtonProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.imageList.myImageList.table',
  });

  return (
    <BlueRoundedButtonWithIcon
      maxWidth="136px"
      onClick={isDownloading ? undefined : onClick}>
      {isDownloading ? (
        <CircularProgress size={30} style={{ color: 'white' }} />
      ) : (
        t('header.download')
      )}
    </BlueRoundedButtonWithIcon>
  );
};
